import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import chefConfig from '../chefConfig';
import Header from './Header';
import useDarkMode from '../hooks/useDarkMode';
import useSocialUser from '../hooks/useSocialUser';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
const ADMIN_UID = 'c2163866-5b15-4e2a-a2db-61b15771646e';

const slugToDisplay = Object.values(chefConfig).reduce((acc, c) => {
  if (c?.slug) acc[c.slug] = c.displayName || c.slug;
  return acc;
}, {});

function fmtKRW(n) {
  if (typeof n !== 'number') return '-';
  return n.toLocaleString('ko-KR') + '원';
}

function fmtDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STATUS_LABEL = { pending: '대기', paid: '지급완료', cancelled: '취소' };

export default function AdminSettlements() {
  const navigate = useNavigate();
  const [darkMode] = useDarkMode();
  const socialUser = useSocialUser();
  const uid = socialUser?.uid || null;
  const isAdmin = uid === ADMIN_UID;

  const [statusFilter, setStatusFilter] = useState('pending');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionBusy, setActionBusy] = useState(null); // settlement_id 처리 중

  useEffect(() => {
    if (!isAdmin) navigate('/', { replace: true });
  }, [isAdmin, navigate]);

  const fetchRows = React.useCallback(() => {
    if (!isAdmin) return;
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ uid });
    if (statusFilter) params.set('status', statusFilter);
    if (creatorFilter) params.set('creator_slug', creatorFilter);
    fetch(`${API_BASE}/admin/settlements?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.ok) setRows(data.settlements || []);
        else setError(data.error || '조회 실패');
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [isAdmin, uid, statusFilter, creatorFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const summary = useMemo(() => {
    const byCreator = {};
    let totalPending = 0;
    let totalPaid = 0;
    for (const r of rows) {
      if (!byCreator[r.creator_slug]) {
        byCreator[r.creator_slug] = { pending: 0, paid: 0, count: 0 };
      }
      byCreator[r.creator_slug].count += 1;
      if (r.status === 'pending') {
        byCreator[r.creator_slug].pending += (r.creator_amount || 0);
        totalPending += (r.creator_amount || 0);
      } else if (r.status === 'paid') {
        byCreator[r.creator_slug].paid += (r.creator_amount || 0);
        totalPaid += (r.creator_amount || 0);
      }
    }
    return { byCreator, totalPending, totalPaid, count: rows.length };
  }, [rows]);

  const handleMarkPaid = async (id) => {
    if (!window.confirm('실제 이체를 완료했나요? 이 작업은 되돌릴 수 없습니다.')) return;
    setActionBusy(id);
    try {
      const res = await fetch(`${API_BASE}/admin/settlements/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, settlement_id: id }),
      });
      const data = await res.json();
      if (data.ok) fetchRows();
      else alert('실패: ' + (data.error || '알 수 없음'));
    } catch (e) {
      alert('네트워크 오류: ' + String(e));
    } finally {
      setActionBusy(null);
    }
  };

  const styles = useMemo(() => makeStyles(darkMode), [darkMode]);

  if (!isAdmin) return null;

  return (
    <div style={styles.page}>
      <Header />
      <div style={styles.container}>
        <h1 style={styles.title}>정산 관리</h1>

        <section style={styles.summaryBox}>
          <div style={styles.summaryRow}>
            <div>
              <div style={styles.summaryLabel}>필터 결과 총 건수</div>
              <div style={styles.summaryValue}>{summary.count}건</div>
            </div>
            <div>
              <div style={styles.summaryLabel}>지급 대기 합계 (크리에이터 몫)</div>
              <div style={{ ...styles.summaryValue, color: darkMode ? '#e0a800' : '#b45309' }}>{fmtKRW(summary.totalPending)}</div>
            </div>
            <div>
              <div style={styles.summaryLabel}>지급 완료 합계</div>
              <div style={{ ...styles.summaryValue, color: darkMode ? '#22c55e' : '#1e7a3d' }}>{fmtKRW(summary.totalPaid)}</div>
            </div>
          </div>

          {Object.keys(summary.byCreator).length > 0 && (
            <div style={styles.creatorSummary}>
              <div style={styles.summaryLabel}>크리에이터별 (대기 / 완료)</div>
              <div style={styles.creatorChips}>
                {Object.entries(summary.byCreator).map(([slug, v]) => (
                  <button
                    key={slug}
                    style={{
                      ...styles.chip,
                      background: creatorFilter === slug ? '#3182f6' : (darkMode ? '#2a2a2a' : '#eef1f5'),
                      color: creatorFilter === slug ? '#fff' : (darkMode ? '#e0e0e0' : '#1a1a1a'),
                    }}
                    onClick={() => setCreatorFilter(creatorFilter === slug ? '' : slug)}
                    title="클릭 시 이 크리에이터만 필터"
                  >
                    {slugToDisplay[slug] || slug}: {fmtKRW(v.pending)} / {fmtKRW(v.paid)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        <div style={styles.filterBar}>
          <label style={styles.filterLabel}>
            상태:&nbsp;
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={styles.select}>
              <option value="pending">대기</option>
              <option value="paid">지급완료</option>
              <option value="cancelled">취소</option>
              <option value="all">전체</option>
            </select>
          </label>
          {creatorFilter && (
            <button onClick={() => setCreatorFilter('')} style={styles.clearBtn}>
              크리에이터 필터 해제 ({slugToDisplay[creatorFilter] || creatorFilter})
            </button>
          )}
          <button onClick={fetchRows} disabled={loading} style={styles.refreshBtn}>
            {loading ? '로딩...' : '새로고침'}
          </button>
        </div>

        {error && <div style={styles.error}>에러: {error}</div>}

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>결제일</th>
                <th style={styles.th}>크리에이터</th>
                <th style={styles.thRight}>총액</th>
                <th style={styles.thRight}>PG수수료</th>
                <th style={styles.thRight}>크리에이터</th>
                <th style={styles.thRight}>플랫폼</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>지급일</th>
                <th style={styles.th}>액션</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={9} style={styles.empty}>결과 없음</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} style={styles.tr}>
                  <td style={styles.td}>{fmtDate(r.created_at)}</td>
                  <td style={styles.td}>{slugToDisplay[r.creator_slug] || r.creator_slug}</td>
                  <td style={styles.tdRight}>{fmtKRW(r.gross_amount)}</td>
                  <td style={styles.tdRight}>{fmtKRW(r.pg_fee)}</td>
                  <td style={{ ...styles.tdRight, color: '#3182f6', fontWeight: 600 }}>{fmtKRW(r.creator_amount)}</td>
                  <td style={styles.tdRight}>{fmtKRW(r.platform_amount)}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, ...statusBadgeStyle(r.status, darkMode) }}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td style={styles.td}>{fmtDate(r.paid_at)}</td>
                  <td style={styles.td}>
                    {r.status === 'pending' && (
                      <button
                        onClick={() => handleMarkPaid(r.id)}
                        disabled={actionBusy === r.id}
                        style={styles.paidBtn}
                      >
                        {actionBusy === r.id ? '처리 중...' : '지급 완료'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.hint}>
          기본 정렬: 최근 생성순 · 최대 500건 · 이체 후 "지급 완료" 클릭 → status/paid_at 업데이트
        </div>
      </div>
    </div>
  );
}

function statusBadgeStyle(status, dark) {
  if (status === 'pending') return dark
    ? { background: '#3a2f00', color: '#e0a800' }
    : { background: '#fef3c7', color: '#b45309' };
  if (status === 'paid') return dark
    ? { background: '#0f3d1f', color: '#4ade80' }
    : { background: '#e6f7ed', color: '#1e7a3d' };
  if (status === 'cancelled') return dark
    ? { background: '#3d0f0f', color: '#f87171' }
    : { background: '#fdecec', color: '#c62b2b' };
  return {};
}

function makeStyles(dark) {
  const bg = dark ? '#111' : '#f5f6f8';
  const text = dark ? '#e0e0e0' : '#1a1a1a';
  const cardBg = dark ? '#1a1a1a' : '#ffffff';
  const cardBorder = dark ? '#2a2a2a' : '#e5e7eb';
  const rowBorder = dark ? '#222' : '#f0f2f5';
  const subText = dark ? 'rgba(224,224,224,0.7)' : '#6b7280';
  const inputBg = dark ? '#2a2a2a' : '#ffffff';
  const inputBorder = dark ? '#444' : '#d1d5db';
  const thBg = dark ? '#141414' : '#f9fafb';
  const errBg = dark ? '#3d0f0f' : '#fdecec';
  const errText = dark ? '#f87171' : '#c62b2b';

  return {
    page: { minHeight: '100vh', background: bg, color: text, boxSizing: 'border-box' },
    container: { maxWidth: 1200, margin: '0 auto', padding: '24px 12px' },
    title: { fontSize: 24, fontWeight: 700, margin: '0 0 20px', color: text },
    summaryBox: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: 16, marginBottom: 16 },
    summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 12 },
    summaryLabel: { fontSize: 12, color: subText, marginBottom: 4 },
    summaryValue: { fontSize: 20, fontWeight: 700, color: text },
    creatorSummary: { borderTop: `1px solid ${cardBorder}`, paddingTop: 12, marginTop: 4 },
    creatorChips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
    chip: { border: 'none', borderRadius: 16, padding: '6px 12px', fontSize: 13, cursor: 'pointer' },
    filterBar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
    filterLabel: { fontSize: 14, color: text },
    select: { padding: '6px 8px', background: inputBg, color: text, border: `1px solid ${inputBorder}`, borderRadius: 4 },
    clearBtn: { padding: '6px 10px', fontSize: 12, background: inputBg, color: text, border: `1px solid ${inputBorder}`, borderRadius: 4, cursor: 'pointer' },
    refreshBtn: { padding: '6px 14px', background: '#3182f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginLeft: 'auto' },
    error: { padding: 12, background: errBg, color: errText, borderRadius: 6, marginBottom: 12 },
    tableWrap: { overflowX: 'auto', background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${cardBorder}`, background: thBg, fontWeight: 600, whiteSpace: 'nowrap', color: text },
    thRight: { textAlign: 'right', padding: '10px 12px', borderBottom: `1px solid ${cardBorder}`, background: thBg, fontWeight: 600, whiteSpace: 'nowrap', color: text },
    tr: { borderBottom: `1px solid ${rowBorder}` },
    td: { padding: '10px 12px', whiteSpace: 'nowrap', color: text },
    tdRight: { padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: text },
    statusBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
    paidBtn: { padding: '4px 10px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
    empty: { textAlign: 'center', padding: 40, color: subText },
    hint: { fontSize: 12, color: subText, marginTop: 12 },
  };
}
